/**
 * Bitácora de una campaña: un renglón por persona con su estado, el motivo si falló o no se mandó,
 * los recibos, si respondió o se dio de baja, y lo que costó.
 *
 * Los números van enmascarados por defecto (decisión D-C): se ven los últimos 4 dígitos
 * (`+54 9 ••• ••• 1234`). El backend ya los manda enmascarados; igual se enmascaran acá cada vez
 * que no se pidió verlos completos, así un backend que se olvide de enmascarar no expone a nadie.
 * «Mostrar números completos» pide `reveal=true`, y el backend registra quién los vio. Es del equipo: a una persona
 * de un cliente no se le ofrece (el backend igual se los contesta enmascarados, 27/10).
 *
 * Una persona de un cliente (Accesos, rol «comercio») ve el motivo de cada fallo en palabras, sin el código al lado, y
 * filtra por «Motivo» (las opciones, sin el código): leía «Se dio de baja de las promociones (131050)» y elegía entre
 * «63024 · …». El equipo ve el motivo con su código, como siempre.
 */

import { useCallback, useEffect, useId, useState } from "react";
import { Eye, Loader2, RefreshCw } from "lucide-react";
import type { EstadoMensajeOficial, FallidoPorCodigo, MensajeFila, PaginaMensajes } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { ESTADO_MENSAJE, MOTIVO_EXCLUSION, motivoFalloParaCliente } from "../logic/textos";
import { destinoParaMostrar, usd } from "../logic/formato";
import { ChipEstadoMensaje } from "./ChipEstadoMensaje";
import { fechaHora, num } from "../../campana-nueva/utils/format";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";

/** Orden del select de estados: el camino feliz primero, después lo que no salió. */
const ESTADOS: EstadoMensajeOficial[] = ["pendiente", "reservado", "aceptado", "enviado", "entregado", "leido", "desconocido",
  "sin_confirmacion", "fallido", "no_entregado", "excluido", "cancelado"];  // sin «control»: ya no hay grupo de control

/**
 * El filtro de los fallidos SIN código (contrato con el backend: `codigo=sin_codigo`). El código vacío
 * no sirve: es «Todos», y `qs()` ni lo manda.
 */
export const CODIGO_SIN_CODIGO = "sin_codigo";

/** El valor con que se filtra una fila de `fallidosPorCodigo`: la sin código va como `sin_codigo`. */
export function valorCodigo(codigo: string): string {
  return codigo || CODIGO_SIN_CODIGO;
}

function motivoFila(m: MensajeFila, equipo: boolean): string {
  if (m.motivoError) return equipo ? m.motivoError : motivoFalloParaCliente(m.motivoError);
  if (m.motivoExclusion) return MOTIVO_EXCLUSION[m.motivoExclusion]?.titulo || m.motivoExclusion;
  return "";
}

const hora = (iso?: string | null) => (iso ? fechaHora(iso) : "—");

interface Props {
  campanaId: string;
  filtroInicial?: { estado?: EstadoMensajeOficial; codigo?: string };
  /** Los códigos de `resultado.fallidosPorCodigo`, para el select de código. */
  codigos?: FallidoPorCodigo[];
  /** id de grupo → nombre (si la página lo sabe). */
  nombresGrupo?: Record<string, string>;
}

export function BitacoraMensajes({ campanaId, filtroInicial, codigos = [], nombresGrupo = {} }: Props) {
  const equipo = useEsEquipoDeenex();
  const idEstado = useId();
  const idCodigo = useId();
  const [estado, setEstado] = useState<EstadoMensajeOficial | "">(filtroInicial?.estado || "");
  const [codigo, setCodigo] = useState<string>(filtroInicial?.codigo || "");
  const [revelar, setRevelar] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState<PaginaMensajes | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const l = leerRespuesta<PaginaMensajes>(await oficialApi.mensajes(campanaId, {
        estado: estado || undefined, codigo: codigo || undefined, page: pagina, limit: 50, reveal: equipo && revelar,
      }));
      if (!l.ok) { setError(l.error || "No pudimos traer los mensajes."); setDatos(null); return; }
      setDatos(l.data);
    } catch (e) {
      setError(`No pudimos traer los mensajes. ${textoError(leerError(e))}`);
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [campanaId, estado, codigo, pagina, revelar, equipo]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Una opción por código: la tabla puede traer el mismo como `fallido` y como `no_entregado`. Un
  // código que vino por «Ver a quiénes» y no está en la lista igual tiene que poder verse elegido.
  const opcionesCodigo = new Map<string, string>();
  for (const c of codigos) {
    const valor = valorCodigo(c.codigo);
    if (!opcionesCodigo.has(valor)) {
      opcionesCodigo.set(valor, !equipo ? (valor === CODIGO_SIN_CODIGO ? "Sin motivo informado" : motivoFalloParaCliente(c.titulo))
        : valor === CODIGO_SIN_CODIGO ? "Sin código" : `${c.codigo} · ${c.titulo}`);
    }
  }
  // A una persona de un cliente, un código que no está en la lista no se le muestra crudo: «Otro motivo».
  if (codigo && !opcionesCodigo.has(codigo)) {
    opcionesCodigo.set(codigo, codigo === CODIGO_SIN_CODIGO ? (equipo ? "Sin código" : "Sin motivo informado") : equipo ? codigo : "Otro motivo");
  }
  const revelado = revelar && datos?.enmascarado === false;
  const paginas = Math.max(1, datos?.paginas || 1);

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label htmlFor={idEstado} className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">Estado</label>
          <select id={idEstado} value={estado} onChange={(e) => { setEstado(e.target.value as EstadoMensajeOficial | ""); setPagina(1); }}
            className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            <option value="">Todos</option>
            {ESTADOS.map((e) => <option key={e} value={e}>{ESTADO_MENSAJE[e].label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={idCodigo} className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">{equipo ? "Código de error" : "Motivo"}</label>
          <select id={idCodigo} value={codigo} onChange={(e) => { setCodigo(e.target.value); setPagina(1); }}
            className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            <option value="">Todos</option>
            {[...opcionesCodigo].map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
          </select>
        </div>
        {equipo && (
          <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
            <input type="checkbox" checked={revelar} onChange={(e) => { setRevelar(e.target.checked); setPagina(1); }} className="accent-violet-600" />
            <Eye size={14} className="text-slate-400" /> Mostrar números completos
          </label>
        )}
        <button type="button" onClick={() => void cargar()} disabled={cargando}
          className="ml-auto h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
          {cargando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar
        </button>
      </div>
      {equipo && revelar && <p className="text-xs text-amber-700">Queda registrado quién lo vio.</p>}

      {error ? (
        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>
      ) : !datos && cargando ? (
        <div className="h-32 rounded-xl bg-slate-50 animate-pulse" />
      ) : !datos?.items?.length ? (
        <p className="py-6 text-center text-sm text-slate-400">No hay mensajes con ese filtro.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th scope="col" className="py-2 pr-3">Destino</th>
                  <th scope="col" className="py-2 pr-3">Grupo</th>
                  <th scope="col" className="py-2 pr-3">Estado</th>
                  <th scope="col" className="py-2 pr-3">Motivo</th>
                  <th scope="col" className="py-2 pr-3">Entregado · leído</th>
                  <th scope="col" className="py-2 pr-3">Respondió</th>
                  <th scope="col" className="py-2 pr-3 text-right">Costo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {datos.items.map((m) => (
                  <tr key={m.id}>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-700 whitespace-nowrap">{destinoParaMostrar(m.destino, revelado)}</td>
                    <td className="py-2 pr-3 text-xs text-slate-600">{nombresGrupo[m.grupoId] || m.grupoId || "—"}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <ChipEstadoMensaje estado={m.estado} />
                        {m.intento > 1 && <span className="text-[10px] text-slate-500">{m.intento}.º intento</span>}
                        {m.simulado && <span className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-amber-50 text-amber-700">simulado</span>}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-600 max-w-[220px]">
                      {motivoFila(m, equipo)}
                      {equipo && m.codigoError && <span className="ml-1 font-mono text-slate-400">({m.codigoError})</span>}
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-slate-500 whitespace-nowrap">{hora(m.entregadoAt)} · {hora(m.leidoAt)}</td>
                    <td className="py-2 pr-3 text-xs">
                      {m.baja ? <span className="font-semibold text-red-600">Baja</span> : m.respondio ? <span className="text-emerald-700">Sí</span> : <span className="text-slate-400">No</span>}
                    </td>
                    <td className="py-2 pr-3 text-right text-xs tabular-nums text-slate-700 whitespace-nowrap">
                      {usd(m.costoClienteUSD, 4)}
                      {/* un mensaje que volvió a la cola se cobra en cada envío: sin esto, 0,0180 con «1.º intento» no se entiende */}
                      {(m.envios ?? 0) > 1 && (
                        <span className="block text-[10px] text-slate-500" title={`Se intentó enviar ${m.envios} veces. El costo suma lo cobrado en cada envío.`}>
                          {m.envios} envíos
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span>{num(datos.total)} {datos.total === 1 ? "mensaje" : "mensajes"}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={pagina <= 1 || cargando} onClick={() => setPagina((p) => Math.max(1, p - 1))}
                className="h-8 px-3 rounded-lg border border-slate-200 bg-white font-semibold disabled:opacity-40">Anterior</button>
              <span>Página {pagina} de {paginas}</span>
              <button type="button" disabled={pagina >= paginas || cargando} onClick={() => setPagina((p) => p + 1)}
                className="h-8 px-3 rounded-lg border border-slate-200 bg-white font-semibold disabled:opacity-40">Siguiente</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BitacoraMensajes;
